import type { ErrorEvent, EventHint } from '@sentry/react'
import { CancelledError } from '@tanstack/react-query'
import { isAxiosError, type AxiosRequestConfig } from 'axios'

const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options'])

export function isNetworkTransportError(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    error.code !== 'ERR_CANCELED' &&
    !error.response &&
    (error.code === 'ERR_NETWORK' || error.message === 'Network Error')
  )
}

export function canRetryNetworkTransportError(
  error: unknown,
  config?: AxiosRequestConfig & { _networkRetry?: boolean }
): boolean {
  if (!config || config._networkRetry) return false
  if (!isNetworkTransportError(error)) return false
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false
  }

  const method = (config.method ?? 'get').toLowerCase()
  return IDEMPOTENT_METHODS.has(method)
}

/**
 * DB admission gate saturation 503 (#1417 Phase 0C): the backend is
 * deliberately shedding load, so a TanStack Query retry would only add to it.
 * `DB_ADMISSION_DISPOSED` (shutdown drain during a rolling deploy) is a
 * DIFFERENT `error` code and must stay retryable — do not fold it in here.
 */
export function isDbSaturation503(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 503) return false
  const body = error.response.data as { error?: string } | undefined
  return body?.error === 'DB_ADMISSION_TIMEOUT'
}

/**
 * QueryClient `retry` option: mirrors TanStack Query's own browser default
 * (retry up to 3 times) except never retries isDbSaturation503. Exported as
 * its own function — not inlined in `main.tsx` — so this policy is
 * unit-testable without mounting the app (`main.tsx` has import-time side
 * effects: Sentry.init, root render, global listeners).
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown
): boolean {
  return !isDbSaturation503(error) && failureCount < 3
}

/** Browser wording for a dynamic `import()` whose chunk never loaded. */
const CHUNK_IMPORT_ERROR_MESSAGES = [
  'failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
]

/**
 * Whether an error is a lazy-route chunk a Cloudflare Pages rollover retired —
 * the `vite:preloadError` reload prompt in `main.tsx` already owns that
 * recovery, so neither `ErrorFallback` nor `isUnhandledChunkImportRejection`
 * reports it. Deliberately a list of
 * the known browser wordings: an error this does not recognise still reports.
 */
export function isChunkImportError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : ''
  const normalized = message.toLowerCase()
  return CHUNK_IMPORT_ERROR_MESSAGES.some((known) => normalized.includes(known))
}

/** The only failure Vite reports for a dependency preload: its `link` error
 *  listener rejects with an Error it builds itself, with this exact wording. */
const CSS_PRELOAD_ERROR_MESSAGE = 'unable to preload css for'

/**
 * Whether a `vite:preloadError` payload may be suppressed — i.e. it is one of
 * the dependency preloads Vite reports through the same event, after which
 * `baseModule()` still runs and the module can still load.
 *
 * Decided by what CAN be suppressed, not by what cannot (Sentry
 * HARMONY-WEB-2V). Vite dispatches that event from two places only: the
 * dependency-preload loop, which always rejects with its own "Unable to
 * preload CSS for <url>", and `baseModule().catch(...)`. Suppressing anything
 * the module fetch rejected with makes that `catch` RESOLVE the dynamic import
 * WITH `undefined`, and the router then reads route options it never got.
 * Listing the browsers' module-fetch wordings instead (`isChunkImportError`)
 * left every other module failure — a `SyntaxError` from a chunk whose body is
 * the SPA fallback, a non-`Error` payload, a reworded browser message — on the
 * suppressed side, which is the side that crashes.
 */
export function isSuppressablePreloadError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : ''
  return message.toLowerCase().includes(CSS_PRELOAD_ERROR_MESSAGE)
}

/**
 * `beforeSend` verdict: a stale lazy-route chunk that reached Sentry's global
 * unhandled-rejection handler (Sentry HARMONY-WEB-6). The router fans one
 * failed `lazyFn()` out over several promise chains and one of them reaches
 * the window unhandled, so the failure `ErrorFallback` already declines (#2119)
 * was still reported from here. Same policy: the `vite:preloadError` reload
 * prompt owns the recovery — the caller checks it was shown. Scoped to that
 * mechanism — a chunk error captured anywhere else, and every other unhandled
 * rejection, still reports. Matched on the suffix: the SDK already renamed it
 * once (v7 `onunhandledrejection`, v10
 * `auto.browser.global_handlers.onunhandledrejection`).
 */
export function isUnhandledChunkImportRejection(
  event: ErrorEvent,
  hint: EventHint
): boolean {
  const mechanisms = event.exception?.values?.map((value) => value.mechanism)
  return (
    !!mechanisms?.some((mechanism) =>
      mechanism?.type?.endsWith('onunhandledrejection')
    ) && isChunkImportError(hint.originalException)
  )
}

/**
 * Whether an error is a query TanStack cancelled on purpose (`silent: true`) —
 * which, in this app, means `activateOrg`'s `queryClient.clear()`: wiping the
 * cache destroys every query, and each one still in flight rejects the promise
 * its suspended route match is awaiting. React rethrows that rejection in the
 * match, so an org switch over a loading screen lands on the error page
 * (Sentry HARMONY-WEB-2Y). Nothing failed: the data was thrown away because we
 * asked for it to be, and re-running the match is the whole recovery.
 *
 * A cancellation WITHOUT `silent` (an `AbortSignal` the caller wired up, a
 * superseded refetch) is left alone — those already surface as real failures
 * where the caller decided they should.
 */
export function isSilentQueryCancellation(error: unknown): boolean {
  return error instanceof CancelledError && error.silent === true
}

/**
 * Whether an error is the API answering "this row does not exist here" (404).
 * A route loader fetches by the id in the URL, so a stale deep link, a deleted
 * row or a hand-edited id lands on the error page with a 404 nobody can fix in
 * code (Sentry HARMONY-WEB-30). `shouldReportQueryError` already rules it a
 * non-incident on the query-cache side; this is the same verdict for the
 * loader throws that reach `ErrorFallback` instead.
 *
 * Deliberately 404-only: the other "expected" statuses mean something the app
 * did (a 400 from a loader is a request the frontend built wrong), so those
 * keep reporting from here.
 */
export function isMissingResourceError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404
}

/**
 * Whether the API refused to hand THIS row to this user: a 404, or a 403 from
 * the ability guard. Another org's id, a deleted row and a mistyped id all land
 * here, and the answer will be the same on every attempt (#2192).
 */
export function isRefusedResourceError(error: unknown): boolean {
  return (
    isMissingResourceError(error) ||
    (isAxiosError(error) && error.response?.status === 403)
  )
}

/**
 * `retry` for a detail read (#2352): a refused id answers the same on every
 * attempt, so it settles at once and the page says « introuvable ou
 * inaccessible » instead of holding a spinner through three backoff retries.
 */
export function retryUnlessRefused(
  failureCount: number,
  error: unknown
): boolean {
  return !isRefusedResourceError(error) && shouldRetryQuery(failureCount, error)
}
